import React from 'react';
import { Listy, theme } from 'antd';
import type { Key } from 'react';

interface SimpleListProps<T> {
  items: T[];
  /** Field name or getter giving each item a stable key. */
  rowKey: keyof T | ((item: T) => Key);
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Row padding: `small` 8px × 16px, `medium` 12px × 24px (the old antd List sizes). */
  size?: 'small' | 'medium';
  /** Draws a divider between rows. */
  split?: boolean;
  /** Draws a rounded border around the whole list. */
  bordered?: boolean;
  /** Removes the horizontal row padding (rows align with surrounding text). */
  flush?: boolean;
}

/**
 * Static list on top of antd `Listy` (the replacement for the deprecated `List`) that keeps the
 * old `List` look: row padding by size, optional dividers and an optional border.
 */
export function SimpleList<T>({
  items,
  rowKey,
  renderItem,
  size = 'small',
  split = true,
  bordered = false,
  flush = false
}: SimpleListProps<T>) {
  const { token } = theme.useToken();
  const paddingBlock = size === 'small' ? token.paddingXS : token.paddingSM;
  const paddingInline = flush ? 0 : size === 'small' ? token.padding : token.paddingLG;

  return (
    <Listy<T>
      items={items}
      rowKey={rowKey}
      styles={{
        root: bordered ? { border: `1px solid ${token.colorBorder}`, borderRadius: token.borderRadiusLG } : undefined,
        // Padding lives on the row wrapper below so dividers span the full row.
        item: { padding: 0 }
      }}
      itemRender={(item, index) => (
        <div
          style={{
            padding: `${paddingBlock}px ${paddingInline}px`,
            borderTop: split && index > 0 ? `1px solid ${token.colorSplit}` : undefined
          }}
        >
          {renderItem(item, index)}
        </div>
      )}
    />
  );
}
